// import { format } from "date-fns";

import { IFilterCheckEligibility, IFilterState } from 'types/Form';

import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { IClaimComponents } from 'types/Claim';
import { FieldAmount, IAmountLimit } from 'types/Member';
import { IMemberSubscriptions } from 'types/Members';
import { useTranslation } from 'utils/translations';
import { SERVICES_SHORT_TRANSLATION_KEY, TEMP_SERVICES } from './constants';
import { getLanguageStorage } from './localStorage';

export const validateEmail = (email: string) => {
	return String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
};

export const camelcase = (str: string) => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

export const camelcaseToUnderscore = (str: string) => {
	if (!str) return '';
	return str
		.split(/(?=[A-Z])/)
		.join('_')
		.toLowerCase();
};

export const pxToRem = (px: number) => {
	return px / 16;
};

export const getStatusString = (string: string) => {
	switch (string) {
		case 'NEW':
		case 'WAITING_FEEDBACK':
		case 'APPROVED':
			return 'Pending';
		case 'MEMBER_PAID':
			return 'Paid';
		case 'COMPLETED':
			return 'Completed';
		case 'CANCELED':
			return 'Canceled';
	}
};

export const getShortDate = (date?: string) => {
	if (!date) return '';
	var d = new Date(date);
	d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);

	return d.toLocaleDateString(getLanguageStorage(), {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});
};

export const getShortTime = (date?: string) => {
	if (!date) return '';
	var d = new Date(date);
	d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);

	return d
		.toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		})
		.toUpperCase();
};

export const getFullDate = (date?: string) => {
	if (!date) return '';

	return `${getShortDate(date)} ${getShortTime(date)}`;
};

export const formatDayjsForBE = (date: dayjs.Dayjs | null) => {
	return dayjs(date).format('YYYY-MM-DD');
};

export const formatZuluForBE = (date: dayjs.Dayjs | null) => {
	return dayjs(date).format('YYYY-MM-DDTHH:mm[Z]') || '';
};

export const formatDateWithoutZulu = (date: string) => {
	return date.substring(0, date.search('T'));
};

export const dayjsForMaterial = (date?: dayjs.Dayjs | null) => {
	if (!date) return undefined;
	return date
		.set('hour', 0)
		.set('minute', 0)
		.set('second', 0)
		.set('millisecond', 0);
};

export const currencyPosition = () => {
	// const exampleString = new Intl.NumberFormat(getLanguageStorage(), { //FIXME: it should be this way but unfortunately it is too hard to explain to BA
	const exampleString = new Intl.NumberFormat('it', {
		style: 'currency',
		currency: 'EUR',
	}).format(1);

	return exampleString.indexOf('€') > exampleString.indexOf('1')
		? 'firstNumber'
		: 'firstCurrency';
};

export const formatPrice = (
	number?: number | string,
	showCurrency: boolean = true,
) => {
	if (number === undefined) return '';
	const n = Number(number);
	return new Intl.NumberFormat(getLanguageStorage(), {
		style: 'currency',
		currency: 'EUR',
	})
		.format(n)
		.replace('-', '- ')
		.replace('€', '')
		.trim()
		.concat(showCurrency ? ' €' : ''); // currency always at the end
};

export const getNumberFromString = (number: string) => {
	return formatNumber(number.replace(/[^\d.,]/g, ''), false);
};

export const formatPercentage = (number?: number | string) => {
	if (number === undefined) return '';
	const n = Number(number);
	return new Intl.NumberFormat(getLanguageStorage(), {
		style: 'percent',
	})
		.format(n / 100)
		.replace('-', '- ')
		.replace('%', '')
		.trim()
		.concat('%'); // symbol always at the end
};

export const formatNumber = (value: string, isCurrency: boolean) => {
	const pattern = /^(:?(?:[+-]?[0-9]+([.,])?([0-9]+)??)+[+-]?)$/;

	if (value.match(pattern)) {
		let returnValue = value.replace(/,/g, '.');
		returnValue = returnValue.replace(/[.](?=.*[.])/g, '');
		const dotPosition = returnValue.search(/\./);

		if (dotPosition !== -1) {
			//se sono stati inseriti 3 caratteri dopo il punto andiamo ad eliminare l'ultimo carattere
			if (dotPosition + 3 < value.length) {
				returnValue = returnValue.substring(0, dotPosition + 3);
			}
		}

		if (isCurrency) {
			returnValue = returnValue.replace('+', '').replace('-', '');
		}

		return returnValue;
	}

	return '';
};

export const formatInputNumberValue = (value: string | number) => {
	const lng = getLanguageStorage();

	if (lng.toLowerCase() === 'en-gb' || lng.toLowerCase() === 'en-us') {
		return value.toString().replace(',', '.');
	} else {
		return value.toString().replace('.', ',');
	}
};

export const copyToClipboard = (text: string) => {
	navigator.clipboard.writeText(text);
};

export const getLocaleLanguage = () => {
	return getLanguageStorage().substring(0, 2);
};

export const bytesToKilobytes = (bytes: number) => {
	return Math.round(bytes / 1024) > 1024
		? [formatNumber(String(bytes / 1024 / 1024), false), 'MB']
		: [Math.round(bytes / 1024), 'kB'];
};

export const buildClaimsFilterParams = (claimsSearchValues: IFilterState) => {
	const {
		inputExternalClaimId,
		inputCarrierClaimId,
		inputFirstName,
		inputLastName,
		dateOfService,
		inputClaimStatus,
	} = claimsSearchValues;

	// Each filter defined once: [queryParamKey, value]
	const filters: [string, string | boolean | null][] = [
		['externalClaimId', inputExternalClaimId || null],
		['carrierClaimId', inputCarrierClaimId || null],
		['firstName', inputFirstName || null],
		['lastName', inputLastName || null],
		['serviceDate', dateOfService ? formatDayjsForBE(dateOfService) : null],
		['claimStatus', inputClaimStatus || null],
	];

	let queryParams = '';
	const body: Record<string, string | boolean | string[] | null> = {};

	for (const [key, value] of filters) {
		if (value === null) continue;

		// Query string
		const qValue =
			typeof value === 'string' ? value.replaceAll(' ', '%20') : value;
		queryParams += `&${key}=${qValue}`;

		// Body
		body[key] = value;
	}

	return { queryParams, body };
};

export const buildPaymentsFilterParams = (
	paymentsSearchValues: IFilterCheckEligibility,
) => {
	const { inputPaymentRunId, inputTotalAmount, inputDateOfService } =
		paymentsSearchValues;

	const filters: [string, string | number | null][] = [
		['paymentRunId', inputPaymentRunId || null],
		['totalAmount', inputTotalAmount || null],
		[
			'paymentRunDate',
			inputDateOfService ? formatDayjsForBE(inputDateOfService) : null,
		],
	];

	let queryParams = '';
	for (const [key, value] of filters) {
		if (value === null) continue;
		const qValue = typeof value === 'string' ? encodeURIComponent(value) : value;
		queryParams += `&${key}=${qValue}`;
	}

	return { queryParams };
};

export const numericValueToTimeString = (value: number | undefined): string => {
	if (typeof value === 'number') {
		const hours = Math.floor(value);
		const minutes = (value - hours) * 60;
		const formattedMinutes = Math.round(minutes);
		return `${hours}:${formattedMinutes.toString().padStart(2, '0')}`;
	} else return '';
};

export const isSubscriptionValid = (
	sub: IMemberSubscriptions,
	inputMemberId: string,
	dateOfService: string,
) => {
	// we want to check the subscriber and external id only if we are searching with the memberId
	if (inputMemberId && inputMemberId !== '') {
		if (
			sub.subscriberMemberId !== inputMemberId &&
			sub.externalMemberId !== inputMemberId
		) {
			return false;
		}
	}

	return (
		dayjs(dateOfService).isBetween(sub.validFrom, sub.validTo, 'day', '[]') ||
		dayjs(sub.validFrom).isSame(dateOfService) ||
		dayjs(sub.validFrom).isAfter(dateOfService)
	);
};

/**
 * Paginate array
 * @param array List of element to be paginated
 * @param page_size PAGE_SIZE const
 * @param page_number Page number requested
 * @returns the slice of array corresponding to that page
 */

export const paginate = (
	array: any[], // TODO: fix any, use generic type array
	pageSize: number,
	pageNumber: number,
) => {
	return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
};

export const getRangeValueAmount = (
	serviceId: number | null,
	benefit: { amountLimits: IAmountLimit[]; serviceId: number }[],
	field: FieldAmount,
) => {
	const values = benefit
		.find(b => b.serviceId === serviceId)
		?.amountLimits.find(amount => amount.field === field);
	return { max: values?.maxLength };
};

export const useGetServiceTypeInitials = () => {
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: 'dbLabels',
	});

	const servicesMap = TEMP_SERVICES;

	const getInitialLabel = useCallback(
		(serviceType: string | undefined) => {
			if (!serviceType) {
				return '-';
			}

			const matching = servicesMap.find(
				service =>
					service.code.toLowerCase().replace('-', '') ===
					serviceType.toLowerCase().replace(' ', ''),
			);

			if (!matching) {
				return '-';
			}

			return tDbLabels(`${matching.label}.${SERVICES_SHORT_TRANSLATION_KEY}`);
		},
		[servicesMap, tDbLabels],
	);

	return {
		getInitialLabel,
	};
};

export const useGetIsOtherComponent = () => {
	const { otherServices } = useSelector((state: RootState) => state.claim);

	const isOtherComponent = (component: IClaimComponents) => {
		return (
			otherServices.map(item => item.serviceId).includes(component.serviceId!) ||
			!component.serviceId
		);
	};

	return { isOtherComponent };
};

export const monthDiff = (d1: Date, d2: Date) => {
	var months;
	months = (d2.getFullYear() - d1.getFullYear()) * 12;
	months -= d1.getMonth();
	months += d2.getMonth();
	if (d2.getDate() <= d1.getDate() && months > 0) {
		months--;
	} else if (d2.getDate() > d1.getDate() && months < 0) {
		months++;
	}
	return Math.abs(months) <= 0 ? 0 : Math.abs(months);
};

export const handleValidationOnlyNumbers = (value: string) => {
	return /^[0-9]+$/i.test(value) || value === '';
};

export const handleValidationLettersNumbers = (value: string) => {
	return /^[a-z0-9 ]+$/i.test(value) || value === '';
};

export const roundingPrice100 = (price: string | number) => {
	return Math.round(parseFloat(price.toString()) * 100);
};

export function formatMarkdown(markdown: string | undefined) {
	if (!markdown) {
		return '';
	}

	// Normalize specific terms using a single, case-insensitive regex
	let formattedText = markdown
		.replace(/\bcovid[\s-]*19\b/gi, 'COVID-19')
		.replace(/\bnaso[\s-]*faringeo\b/gi, 'NASO-FARINGEO')
		.replace(/S\.\s*S\.\s*N\./g, 'S.S.N.')
		.replace(/oppure\s*\n\s*-\s*/g, 'oppure\n* ');

	formattedText = replaceVowelWithAccent(formattedText);

	// Standardize list bullet points
	if (
		!formattedText.includes('COVID-19') &&
		!formattedText.includes('NASO-FARINGEO') &&
		!formattedText.includes('ozono') &&
		!formattedText.includes('OPT') &&
		!formattedText.includes('fisiokinesiterapia')
	) {
		formattedText = formattedText.replace(/(?:\n)?\s*[-*•]\s*/g, '\n* ');
	}

	// Convert multi-line paragraphs into single lines for better formatting.
	formattedText = formattedText.replaceAll('\n', ' \n\n');

	formattedText = formattedText
		.replace(/\b\w+\s,/g, match => match.replace(/,/g, '')) // replace [word ,] with [word, ]
		.replace(/(,|')([a-zA-Z])/g, '$1 $2'); // adds a space after a comma or apostrophe if it is followed by a letter

	// Handle special case for faschim website
	if (!formattedText.includes('www.faschim.it')) {
		formattedText = formattedText.replace(/(\s+\.\s*)([a-zA-Z0-9])/g, '. $2'); // adds a space after a period if it is followed by a letter or number
	}

	// Handle the special case of a colon followed by a list.
	// This looks for a colon followed by a newline and a list item, then adds a new line before it.
	formattedText = formattedText.replace(/(\s*:)\n(\*)/g, '$1\n\n$2');

	// Remove double special characters
	formattedText = formattedText.replace(/""|\.{2,}/g, match =>
		match.startsWith('"') ? '' : '.',
	);

	// Remove multiple spaces
	formattedText = formattedText.replace(/\s{5,}/g, ' ');

	// handle special case \\n
	formattedText = formattedText.replace(/\\n/g, '\n');

	return formattedText.trim();
}

function replaceVowelWithAccent(str: string) {
	let formattedSpace = str.replace(/'(?! )/g, "' ");

	let result = formattedSpace.replace(
		/[aA]'|[eE]'|[iI]'|[oO]'|[uU]'/g,
		match => {
			switch (match) {
				case "a'":
					return 'à';
				case "A'":
					return 'À';
				case "e'":
					return 'è';
				case "E'":
					return 'È';
				case "i'":
					return 'ì';
				case "I'":
					return 'Ì';
				case "o'":
					return 'ò';
				case "O'":
					return 'Ò';
				case "u'":
					return 'ù';
				case "U'":
					return 'Ù';
				default:
					return match;
			}
		},
	);

	return result;
}

export const serviceIdToLabel = (serviceId: number) => {
	const prefix = 'newClaim.priceEstimate.';

	switch (serviceId) {
		case 1:
			return `${prefix}lenses`;
		case 2:
			return `${prefix}frame`;
		case 3:
			return `${prefix}exam`;
		case 4:
			return `${prefix}frameAndLenses`;
		case 5:
			return `${prefix}contacts`;
		case 12:
			return `${prefix}lensesMonofocal`;
		case 13:
			return `${prefix}lensesBifocal`;
		case 14:
			return `${prefix}lensesTrifocal`;
		case 15:
			return `${prefix}lensesProgressive`;
		case 16:
			return `${prefix}frameAndLensesMonofocal`;
		case 17:
			return `${prefix}frameAndLensesBifocal`;
		case 18:
			return `${prefix}frameAndLensesTrifocal`;
		case 19:
			return `${prefix}frameAndLensesProgressive`;
		case 20:
			return `${prefix}examVdu`;
		default:
			return '';
	}
};
